import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, TrendingUp, Users, Building2, MapPin, BarChart3, 
  Briefcase, Home, DollarSign, Target, Shield, ChevronRight, ChevronLeft,
  Factory, Anchor, Plane, Stethoscope, Cpu, PiggyBank
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

// Animated counter component
const AnimatedCounter = ({ value, suffix = '', prefix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    const numericValue = parseFloat(value.toString().replace(/[^0-9.]/g, ''));
    const steps = 60;
    const stepDuration = duration / steps;
    const increment = numericValue / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= numericValue) {
        setCount(numericValue);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [isInView, value, duration]);

  return (
    <span ref={ref}>
      {prefix}{typeof value === 'string' && value.includes('.') ? count.toFixed(1) : count}{suffix}
    </span>
  );
};

const WhyHoustonPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
    }
  };

  // Houston stats data
  const houstonStats = [
    { value: 7.5, suffix: 'M+', label: 'Metro Population', icon: Users },
    { value: 4, suffix: 'th', label: 'Largest US City', icon: Building2 },
    { value: 170, suffix: 'K+', label: 'Annual New Residents', icon: TrendingUp },
    { value: 26, suffix: '+', label: 'Fortune 500 HQs', icon: Briefcase }
  ];

  // Economic sectors with percentages for infographic and images
  const economicSectors = [
    { 
      icon: Factory, 
      name: 'Energy & Petrochemical', 
      description: 'Global energy capital with major oil & gas headquarters', 
      percentage: 28,
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/doeea382_EOQRZUDOGRKMRNL6HVOCCRUXPI.jpg'
    },
    { 
      icon: Stethoscope, 
      name: 'Healthcare & Medical', 
      description: 'Texas Medical Center - largest medical complex worldwide', 
      percentage: 22,
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/ceoq2g06_e75c37cb-e7ba-436f-b5cd-6ba8ed48b732.png'
    },
    { 
      icon: Anchor, 
      name: 'Port & Logistics', 
      description: 'Port of Houston - #1 US port in foreign tonnage', 
      percentage: 18,
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/neqwbucz_4e8d5fb3-fd44-4120-b85a-253c117f5170.png'
    },
    { 
      icon: Plane, 
      name: 'Aerospace & Aviation', 
      description: 'NASA Johnson Space Center and aerospace manufacturing', 
      percentage: 12,
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/7ubdfd35_76dad965-68f7-421e-a547-8274bccef562.png'
    },
    { 
      icon: Cpu, 
      name: 'Technology & Innovation', 
      description: 'Growing tech hub with major corporate expansions', 
      percentage: 12,
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/ufnfticu_dfcdcd06-ec88-407b-a009-206c562da884.png'
    },
    { 
      icon: PiggyBank, 
      name: 'Finance & Banking', 
      description: 'Regional banking center with diverse financial services', 
      percentage: 8,
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/fvhsdc05_1ded672c-bf25-4ae4-a41a-04ad15482bff.png'
    }
  ];

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-scroll carousel every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % economicSectors.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, economicSectors.length]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % economicSectors.length);
  }, [economicSectors.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + economicSectors.length) % economicSectors.length);
  }, [economicSectors.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  // Economic highlights for infographic
  const economicHighlights = [
    { value: '$512B', label: 'Metro GDP', color: '#D4AF37' },
    { value: '3.5M+', label: 'Jobs', color: '#F5D76E' },
    { value: '5,200+', label: 'Energy Firms', color: '#B8962E' },
    { value: '$100B+', label: 'Annual Trade', color: '#8B7355' }
  ];

  // Multifamily fundamentals
  const multifamilyStats = [
    { metric: 'Occupancy Rate', value: '93%+', trend: 'Stable' },
    { metric: 'Annual Rent Growth', value: '3-5%', trend: 'Consistent' },
    { metric: 'Workforce Housing Demand', value: 'High', trend: 'Growing' },
    { metric: 'Supply Pipeline', value: 'Moderate', trend: 'Balanced' }
  ];

  // Market comparison data
  const marketComparison = [
    { market: 'Houston', capRate: '5.5-7%', pricePerUnit: '$90-130K', population: '7.5M', growth: 'High' },
    { market: 'Dallas', capRate: '5-6%', pricePerUnit: '$120-160K', population: '7.6M', growth: 'High' },
    { market: 'Austin', capRate: '4.5-5.5%', pricePerUnit: '$150-200K', population: '2.4M', growth: 'Very High' },
    { market: 'San Antonio', capRate: '5.5-6.5%', pricePerUnit: '$80-110K', population: '2.6M', growth: 'Moderate' }
  ];

  // Submarkets with image placeholders
  const submarkets = [
    { 
      name: 'Sugar Land', 
      type: 'Affluent Suburban', 
      highlight: 'High-income demographics, strong schools, corporate relocations',
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/i3lwfnep_1.png'
    },
    { 
      name: 'Baytown', 
      type: 'Industrial Growth', 
      highlight: 'Port expansion, petrochemical investment, workforce housing demand',
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/161pg1v8_2.png'
    },
    { 
      name: 'Katy', 
      type: 'Family-Oriented', 
      highlight: 'Rapid residential growth, retail development, top-rated schools',
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/pkvafomf_3.png'
    },
    { 
      name: 'The Woodlands', 
      type: 'Master-Planned', 
      highlight: 'Corporate HQs, high-income demographics, premium rental market',
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/p4xba0m4_4.png'
    },
    { 
      name: 'Pearland', 
      type: 'Emerging Suburban', 
      highlight: 'Healthcare expansion, young professionals, growing retail',
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/tt0lqfhe_5.png'
    },
    { 
      name: 'Cypress', 
      type: 'Growth Corridor', 
      highlight: 'New construction, population influx, expanding infrastructure',
      image: 'https://customer-assets.emergentagent.com/job_c228e42e-916f-4d2b-8871-4d4297ae279b/artifacts/a876bhpx_6.png'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Why Invest in Houston Real Estate | Market Outlook & Trends</title>
        <meta name="description" content="Explore why Houston remains a top real estate investment market, supported by population growth, job diversity, and resilient multifamily demand." />
        <meta property="og:title" content="Why Invest in Houston Real Estate | Market Outlook & Trends" />
        <meta property="og:description" content="Explore why Houston remains a top real estate investment market, supported by population growth, job diversity, and resilient multifamily demand." />
        <link rel="canonical" href="/why-houston" />
      </Helmet>

      <div className="bg-[#0A1628] min-h-screen">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-[#D4AF37]/3 rounded-full blur-[100px] pointer-events-none" />

            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
              >
                {/* Left Column - Content */}
                <div className="max-w-xl">
                  {/* H1 */}
                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-8"
                    variants={itemVariants}
                  >
                    Why Invest in{' '}
                    <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                      Houston Real Estate
                    </span>?
                  </motion.h1>

                  <motion.p 
                    className="text-base lg:text-lg text-white/60 leading-relaxed mb-10"
                    variants={itemVariants}
                  >
                    Discover why Houston remains one of America's most attractive markets for real estate investment, 
                    backed by robust economic fundamentals, sustained population growth, and resilient rental demand.
                  </motion.p>

                  {/* CTA Buttons */}
                  <motion.div 
                    className="flex flex-wrap gap-4"
                    variants={itemVariants}
                  >
                    <Link 
                      to="/contact"
                      
                      
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2 shadow-lg shadow-[#D4AF37]/20">
                        Schedule Consultation
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/portfolio">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                        View Portfolio
                      </Button>
                    </Link>
                  </motion.div>
                </div>

                {/* Right Column - Houston Image with Floating Stats */}
                <motion.div 
                  className="relative"
                  variants={itemVariants}
                >
                  {/* Houston Skyline Image */}
                  <motion.div 
                    className="relative rounded-3xl overflow-hidden shadow-2xl shadow-black/30"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <img 
                      src="https://images.unsplash.com/photo-1651069674563-b1f057020cc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxIb3VzdG9uJTIwc2t5bGluZSUyMGRvd250b3duJTIwZGF5dGltZXxlbnwwfHx8fDE3Njk0NDMzMzZ8MA&ixlib=rb-4.1.0&q=85"
                      alt="Houston skyline"
                      className="w-full h-[400px] lg:h-[450px] object-cover"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-transparent to-[#0A1628]/20" />
                  </motion.div>

                  {/* Floating Stat Card 1 - Top Left */}
                  <motion.div
                    className="absolute -top-4 -left-4 lg:-left-8 bg-[#111D35]/90 backdrop-blur-md border border-white/10 rounded-xl p-4 shadow-xl"
                    initial={{ opacity: 0, x: -20, y: -20 }}
                    animate={{ 
                      opacity: 1, 
                      x: 0, 
                      y: [0, -6, 0],
                    }}
                    transition={{ 
                      opacity: { delay: 0.5, duration: 0.6 },
                      x: { delay: 0.5, duration: 0.6 },
                      y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                    }}
                  >
                    <Users className="h-5 w-5 text-[#D4AF37] mb-2" />
                    <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
                      <AnimatedCounter value={7.5} suffix="M+" />
                    </div>
                    <p className="text-white/60 text-xs">Metro Population</p>
                  </motion.div>

                  {/* Floating Stat Card 2 - Top Right */}
                  <motion.div
                    className="absolute -top-2 right-4 lg:right-8 bg-[#111D35]/90 backdrop-blur-md border border-white/10 rounded-xl p-4 shadow-xl"
                    initial={{ opacity: 0, x: 20, y: -20 }}
                    animate={{ 
                      opacity: 1, 
                      x: 0, 
                      y: [0, -5, 0],
                    }}
                    transition={{ 
                      opacity: { delay: 0.7, duration: 0.6 },
                      x: { delay: 0.7, duration: 0.6 },
                      y: { duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }
                    }}
                  >
                    <Building2 className="h-5 w-5 text-[#D4AF37] mb-2" />
                    <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
                      4<span className="text-lg">th</span>
                    </div>
                    <p className="text-white/60 text-xs">Largest US City</p>
                  </motion.div>

                  {/* Floating Stat Card 3 - Bottom Left */}
                  <motion.div
                    className="absolute bottom-8 -left-2 lg:-left-6 bg-[#111D35]/90 backdrop-blur-md border border-white/10 rounded-xl p-4 shadow-xl"
                    initial={{ opacity: 0, x: -20, y: 20 }}
                    animate={{ 
                      opacity: 1, 
                      x: 0, 
                      y: [0, -7, 0],
                    }}
                    transition={{ 
                      opacity: { delay: 0.9, duration: 0.6 },
                      x: { delay: 0.9, duration: 0.6 },
                      y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }
                    }}
                  >
                    <TrendingUp className="h-5 w-5 text-[#D4AF37] mb-2" />
                    <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
                      <AnimatedCounter value={170} suffix="K+" />
                    </div>
                    <p className="text-white/60 text-xs">Annual New Residents</p>
                  </motion.div>

                  {/* Floating Stat Card 4 - Bottom Right */}
                  <motion.div
                    className="absolute -bottom-4 right-8 lg:right-12 bg-[#111D35]/90 backdrop-blur-md border border-white/10 rounded-xl p-4 shadow-xl"
                    initial={{ opacity: 0, x: 20, y: 20 }}
                    animate={{ 
                      opacity: 1, 
                      x: 0, 
                      y: [0, -6, 0],
                    }}
                    transition={{ 
                      opacity: { delay: 1.1, duration: 0.6 },
                      x: { delay: 1.1, duration: 0.6 },
                      y: { duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: 1.5 }
                    }}
                  >
                    <Briefcase className="h-5 w-5 text-[#D4AF37] mb-2" />
                    <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
                      <AnimatedCounter value={26} suffix="+" />
                    </div>
                    <p className="text-white/60 text-xs">Fortune 500 HQs</p>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== ECONOMIC GROWTH CAROUSEL ==================== */}
          <section className="py-16 lg:py-24 relative mt-8">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-20">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Economic Foundation
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Economic Growth & Job Diversity
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl"
                    variants={itemVariants}
                  >
                    Houston's diversified economy spans energy, healthcare, aerospace, and technology, 
                    creating stable employment and sustained housing demand.
                  </motion.p>
                </div>

                {/* Economic Highlights Stats */}
                <motion.div 
                  className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
                  variants={itemVariants}
                >
                  {economicHighlights.map((highlight, index) => (
                    <motion.div
                      key={highlight.label}
                      className="bg-[#111D35]/50 backdrop-blur-sm border border-white/10 rounded-xl p-5 group hover:border-[#D4AF37]/30 transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -4 }}
                    >
                      <div 
                        className="text-2xl lg:text-3xl font-bold mb-1"
                        style={{ color: highlight.color }}
                      >
                        {highlight.value}
                      </div>
                      <p className="text-white/50 text-sm">{highlight.label}</p>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Economic Sectors Carousel */}
                <div 
                  className="relative"
                  onMouseEnter={() => setIsAutoPlaying(false)}
                  onMouseLeave={() => setIsAutoPlaying(true)}
                >
                  {/* Navigation Arrows - Outside the box */}
                  <button
                    onClick={prevSlide}
                    className="absolute -left-6 lg:-left-16 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#111D35] border border-white/10 rounded-full flex items-center justify-center text-white hover:bg-[#D4AF37]/20 hover:border-[#D4AF37]/30 transition-all duration-300 z-10"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute -right-6 lg:-right-16 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#111D35] border border-white/10 rounded-full flex items-center justify-center text-white hover:bg-[#D4AF37]/20 hover:border-[#D4AF37]/30 transition-all duration-300 z-10"
                    aria-label="Next slide"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>

                  {/* Carousel Container */}
                  <div className="relative rounded-3xl overflow-hidden h-[400px] lg:h-[450px]">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentSlide}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        className="absolute inset-0"
                      >
                        {/* Background Image */}
                        <img 
                          src={economicSectors[currentSlide].image}
                          alt={economicSectors[currentSlide].name}
                          className="w-full h-full object-cover"
                        />
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/70 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/80 via-transparent to-[#0A1628]/40" />
                        
                        {/* Content */}
                        <div className="absolute inset-0 flex items-center">
                          <div className="px-8 lg:px-12 max-w-xl">
                            {/* Icon */}
                            <motion.div 
                              className="w-16 h-16 bg-[#D4AF37]/20 rounded-2xl flex items-center justify-center mb-6"
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 0.2, duration: 0.4 }}
                            >
                              {React.createElement(economicSectors[currentSlide].icon, {
                                className: "h-8 w-8 text-[#D4AF37]"
                              })}
                            </motion.div>
                            
                            {/* Percentage */}
                            <motion.div 
                              className="flex items-baseline gap-2 mb-4"
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: 0.3, duration: 0.4 }}
                            >
                              <span className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
                                {economicSectors[currentSlide].percentage}%
                              </span>
                              <span className="text-white/50 text-lg">of Economy</span>
                            </motion.div>
                            
                            {/* Title */}
                            <motion.h3 
                              className="text-2xl lg:text-3xl font-bold text-white mb-3"
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: 0.4, duration: 0.4 }}
                            >
                              {economicSectors[currentSlide].name}
                            </motion.h3>
                            
                            {/* Description */}
                            <motion.p 
                              className="text-white/70 text-base lg:text-lg leading-relaxed mb-6"
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: 0.5, duration: 0.4 }}
                            >
                              {economicSectors[currentSlide].description}
                            </motion.p>
                            
                            {/* Progress Bar */}
                            <motion.div 
                              className="w-full max-w-xs"
                              initial={{ scaleX: 0, opacity: 0 }}
                              animate={{ scaleX: 1, opacity: 1 }}
                              transition={{ delay: 0.6, duration: 0.4 }}
                            >
                              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                <motion.div
                                  className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] rounded-full"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${economicSectors[currentSlide].percentage}%` }}
                                  transition={{ delay: 0.7, duration: 0.8, ease: "easeOut" }}
                                />
                              </div>
                            </motion.div>
                          </div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Dot Indicators */}
                  <div className="flex justify-center gap-2 mt-6">
                    {economicSectors.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          index === currentSlide 
                            ? 'bg-[#D4AF37] w-8' 
                            : 'bg-white/20 hover:bg-white/40'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== POPULATION GROWTH ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                {/* Content */}
                <div>
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Demographic Drivers
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-6"
                    variants={itemVariants}
                  >
                    Population Growth & Housing Demand
                  </motion.h2>
                  <motion.div className="space-y-4 text-white/60 leading-relaxed" variants={itemVariants}>
                    <p>
                      The Greater Houston metropolitan area continues to attract over 170,000 new residents annually, 
                      driven by job opportunities, affordable cost of living, and business-friendly environment.
                    </p>
                    <p>
                      This sustained in-migration creates consistent demand for workforce housing, particularly 
                      in Class B and C multifamily properties that serve the region's growing middle-income population.
                    </p>
                  </motion.div>

                  {/* Growth indicators */}
                  <motion.div 
                    className="grid grid-cols-2 gap-4 mt-8"
                    variants={itemVariants}
                  >
                    <div className="bg-[#111D35]/50 border border-white/10 rounded-lg p-4">
                      <div className="text-2xl font-bold text-[#D4AF37]">
                        <AnimatedCounter value={2.1} suffix="%" />
                      </div>
                      <p className="text-white/50 text-sm">Annual Population Growth</p>
                    </div>
                    <div className="bg-[#111D35]/50 border border-white/10 rounded-lg p-4">
                      <div className="text-2xl font-bold text-[#D4AF37]">
                        <AnimatedCounter value={65} suffix="K" />
                      </div>
                      <p className="text-white/50 text-sm">New Households/Year</p>
                    </div>
                  </motion.div>
                </div>

                {/* Visual - Population Growth Chart */}
                <motion.div 
                  className="relative"
                  variants={itemVariants}
                >
                  <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 lg:p-8 border border-white/10">
                    <h4 className="text-white font-semibold mb-6 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-[#D4AF37]" />
                      Houston Metro Population Growth
                    </h4>
                    
                    {/* Animated bar chart */}
                    <div className="space-y-4">
                      {[
                        { year: '2015', value: 6.5, width: '65%' },
                        { year: '2018', value: 6.9, width: '75%' },
                        { year: '2021', value: 7.1, width: '85%' },
                        { year: '2024', value: 7.5, width: '95%' },
                        { year: '2027E', value: 8.0, width: '100%' }
                      ].map((data, index) => (
                        <div key={data.year} className="flex items-center gap-4">
                          <span className="text-white/50 text-sm w-16">{data.year}</span>
                          <div className="flex-1 h-8 bg-[#0A1628] rounded-lg overflow-hidden">
                            <motion.div
                              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] rounded-lg flex items-center justify-end pr-3"
                              initial={{ width: 0 }}
                              whileInView={{ width: data.width }}
                              viewport={{ once: true }}
                              transition={{ delay: index * 0.15, duration: 0.8, ease: "easeOut" }}
                            >
                              <span className="text-[#0A1628] text-xs font-bold">{data.value}M</span>
                            </motion.div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-white/30 text-xs mt-4 text-right">Source: US Census Bureau, Projections</p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MULTIFAMILY FUNDAMENTALS ==================== */}
          <section className="py-16 lg:py-24 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Market Fundamentals
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Strong Multifamily Fundamentals
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl"
                    variants={itemVariants}
                  >
                    Houston's rental market demonstrates resilient occupancy rates and consistent rent growth, 
                    particularly in workforce housing segments.
                  </motion.p>
                </div>

                {/* Fundamentals Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {multifamilyStats.map((stat, index) => (
                    <motion.div
                      key={stat.metric}
                      className="relative bg-[#111D35]/50 backdrop-blur-sm border border-white/10 rounded-xl p-6 overflow-hidden group hover:border-[#D4AF37]/30 transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      {/* Background glow */}
                      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      
                      <div className="relative z-10">
                        <p className="text-white/40 text-sm mb-2">{stat.metric}</p>
                        <p className="text-3xl font-bold text-white mb-2">{stat.value}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          stat.trend === 'Growing' ? 'bg-green-500/20 text-green-400' :
                          stat.trend === 'Stable' ? 'bg-blue-500/20 text-blue-400' :
                          stat.trend === 'Consistent' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                          'bg-white/10 text-white/60'
                        }`}>
                          {stat.trend}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== VALUE-ADD OPPORTUNITIES ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                {/* Visual */}
                <motion.div 
                  className="order-2 lg:order-1"
                  variants={itemVariants}
                >
                  <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 lg:p-8 border border-white/10">
                    <h4 className="text-white font-semibold mb-6 flex items-center gap-2">
                      <Target className="h-5 w-5 text-[#D4AF37]" />
                      Value-Add Investment Criteria
                    </h4>
                    
                    <div className="space-y-4">
                      {[
                        { label: 'Aging Properties (15-30 years)', value: 85 },
                        { label: 'Below-Market Rents', value: 75 },
                        { label: 'Operational Inefficiencies', value: 70 },
                        { label: 'Deferred Maintenance', value: 80 },
                        { label: 'Management Opportunity', value: 90 }
                      ].map((item, index) => (
                        <div key={item.label}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-white/70">{item.label}</span>
                            <span className="text-[#D4AF37]">{item.value}%</span>
                          </div>
                          <div className="h-2 bg-[#0A1628] rounded-full overflow-hidden">
                            <motion.div
                              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] rounded-full"
                              initial={{ width: 0 }}
                              whileInView={{ width: `${item.value}%` }}
                              viewport={{ once: true }}
                              transition={{ delay: 0.2 + index * 0.1, duration: 0.8 }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Investment Thesis
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-6"
                    variants={itemVariants}
                  >
                    Value-Add Investment Opportunities
                  </motion.h2>
                  <motion.div className="space-y-4 text-white/60 leading-relaxed" variants={itemVariants}>
                    <p>
                      Houston's extensive inventory of aging multifamily properties presents significant 
                      value-add opportunities for experienced operators. Many Class B and C properties 
                      suffer from deferred maintenance and below-market rents.
                    </p>
                    <p>
                      Our approach focuses on acquiring distressed or underperforming assets at below-market 
                      prices, implementing strategic renovations, and improving operational efficiency to 
                      unlock substantial value for investors.
                    </p>
                  </motion.div>

                  <motion.div className="mt-6" variants={itemVariants}>
                    <Link to="/strategy">
                      <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-6 py-3 rounded-full text-sm">
                        View Investment Strategy
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== SUBMARKETS ==================== */}
          <section className="py-16 lg:py-24 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Geographic Diversity
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Diverse Submarkets Across Houston
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl"
                    variants={itemVariants}
                  >
                    Greater Houston offers distinct investment opportunities across suburban growth corridors, 
                    each with unique demand drivers and growth characteristics.
                  </motion.p>
                </div>

                {/* Submarkets Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {submarkets.map((submarket, index) => (
                    <motion.div
                      key={submarket.name}
                      className="bg-[#111D35]/50 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-300 group"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.08 }}
                      whileHover={{ y: -5 }}
                    >
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={submarket.image}
                          alt={`${submarket.name} area`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/80 via-transparent to-transparent" />
                        <div className="absolute top-3 left-3">
                          <MapPin className="h-5 w-5 text-[#D4AF37]" />
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-5">
                        <h3 className="text-white font-semibold text-lg mb-1 group-hover:text-[#D4AF37] transition-colors">
                          {submarket.name}
                        </h3>
                        <p className="text-[#D4AF37]/70 text-xs mb-3 uppercase tracking-wider">{submarket.type}</p>
                        <p className="text-white/60 text-sm leading-relaxed">{submarket.highlight}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MARKET COMPARISON ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Competitive Analysis
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Houston vs Other Texas Markets
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl"
                    variants={itemVariants}
                  >
                    Compare Houston's investment fundamentals against other major Texas metropolitan areas.
                  </motion.p>
                </div>

                {/* Comparison Table */}
                <motion.div 
                  className="overflow-x-auto"
                  variants={itemVariants}
                >
                  <div className="bg-[#111D35]/50 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden min-w-[600px]">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left p-4 text-white font-semibold">Market</th>
                          <th className="text-center p-4 text-white/60 text-sm">Cap Rate</th>
                          <th className="text-center p-4 text-white/60 text-sm">Price/Unit</th>
                          <th className="text-center p-4 text-white/60 text-sm">Population</th>
                          <th className="text-center p-4 text-white/60 text-sm">Growth</th>
                        </tr>
                      </thead>
                      <tbody>
                        {marketComparison.map((market, index) => (
                          <motion.tr 
                            key={market.market}
                            className={`border-b border-white/5 ${market.market === 'Houston' ? 'bg-[#D4AF37]/5' : ''}`}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <td className="p-4">
                              <span className={`font-semibold ${market.market === 'Houston' ? 'text-[#D4AF37]' : 'text-white'}`}>
                                {market.market}
                              </span>
                            </td>
                            <td className="text-center p-4 text-white/70">{market.capRate}</td>
                            <td className="text-center p-4 text-white/70">{market.pricePerUnit}</td>
                            <td className="text-center p-4 text-white/70">{market.population}</td>
                            <td className="text-center p-4">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                market.growth === 'High' ? 'bg-green-500/20 text-green-400' :
                                market.growth === 'Very High' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-white/10 text-white/60'
                              }`}>
                                {market.growth}
                              </span>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>

                <motion.p 
                  className="text-white/30 text-xs mt-4 text-center"
                  variants={itemVariants}
                >
                  Data as of Q4 2024. Cap rates and pricing vary by property class and submarket.
                </motion.p>
              </motion.div>
            </div>
          </section>

          {/* ==================== LOCAL EXPERTISE ==================== */}
          <section className="py-16 lg:py-24 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                <div>
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Competitive Advantage
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-6"
                    variants={itemVariants}
                  >
                    Why Local Expertise Matters
                  </motion.h2>
                  <motion.div className="space-y-4 text-white/60 leading-relaxed" variants={itemVariants}>
                    <p>
                      In a market as large and diverse as Houston, local knowledge is essential for identifying 
                      opportunities and navigating market cycles. Our team's deep roots in the Greater Houston 
                      area provide crucial advantages.
                    </p>
                  </motion.div>

                  {/* Expertise points */}
                  <motion.div className="mt-6 space-y-3" variants={itemVariants}>
                    {[
                      'Off-market deal sourcing through established broker relationships',
                      'Submarket-level insight for property selection',
                      'Understanding of local regulatory environment',
                      'Proven contractor and vendor networks',
                      'Real-time market intelligence and timing discipline'
                    ].map((point, index) => (
                      <motion.div
                        key={index}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Shield className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <span className="text-white/70 text-sm">{point}</span>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>

                {/* Years of experience card */}
                <motion.div 
                  className="relative"
                  variants={itemVariants}
                >
                  <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#111D35] rounded-2xl p-8 lg:p-10 border border-[#D4AF37]/20 text-center">
                    <div className="text-6xl lg:text-7xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent mb-4">
                      <AnimatedCounter value={18} suffix="+" />
                    </div>
                    <p className="text-white text-xl font-semibold mb-2">Years in Houston</p>
                    <p className="text-white/50">
                      Building expertise, relationships, and track record across the Greater Houston real estate market since 2005.
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== CTA SECTION ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-10 lg:p-14 border border-white/10 text-center relative overflow-hidden"
              >
                {/* Background elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-[100px]" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#D4AF37]/5 rounded-full blur-[80px]" />
                
                <div className="relative z-10">
                  <Building2 className="h-12 w-12 text-[#D4AF37] mx-auto mb-4" />
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                    Explore Our Houston Portfolio
                  </h2>
                  <p className="text-white/50 max-w-xl mx-auto mb-8">
                    See how we've applied our Houston market expertise to create value across our portfolio 
                    of multifamily and commercial properties.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/portfolio">
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8 py-3 h-12 rounded-full text-sm flex items-center gap-2 shadow-lg shadow-[#D4AF37]/20">
                          View Portfolio
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </motion.div>
                    </Link>
                    <Link to="/track-record">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-12 rounded-full text-sm">
                        Track Record
                      </Button>
                    </Link>
                    <Link 
                      to="/contact"
                      
                      
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-3 h-12 rounded-full text-sm">
                        Schedule Consultation
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default WhyHoustonPage;
